import type { ReactNode } from 'react';
import { useState } from 'react';
import { useSegment } from '@repo/poly-hooks/segment';
import type { IdNowErrorKeys } from '../idnow';
import { IdNowError } from '../idnow/error';
import { FraudConfirmed, FraudSuspicion } from '../idnow/fraud';
import { StartedOnPhone } from '../idnow/started-on-phone';
import { browser, isMobile } from '../../utils/device';
import { WaitingPopup } from '../idnow/popup';
import { Iframe } from '../idnow/iframe';
import { DownloadApp } from './download-app';
import { LandingMobile } from './landing-mobile';
import { LandingDesktop } from './landing-desktop';
import { ConsentPopup } from './consent-popup';

type IdNowStatus =
  | 'pending'
  | 'on_call'
  | 'waiting_results'
  | 'succeeded'
  | 'canceled'
  | 'rejected'
  | 'failed';

export function IDNow2({
  status,
  tryAgain,
  token,
  onSendSMS,
  reason,
  qrCodeUrl,
  hasGermanResidencePermit,
  onBack,
}: {
  token: string;
  status: IdNowStatus;
  tryAgain: () => void;
  onSendSMS: () => void;
  qrCodeUrl: string;
  hasGermanResidencePermit: boolean;
  reason?: IdNowErrorKeys;
  onBack?: () => void;
}): ReactNode {
  const [state, setState] = useState<'landing' | 'consent' | 'download-app' | 'video-call'>(
    'landing'
  );
  const segment = useSegment();

  const src = `${process.env.IDNOW_IFRAME_DOMAIN}/web/qontovideo/identifications/${token}/language-selection`;

  // Shared statuses
  if (status === 'canceled' || status === 'failed') {
    return (
      <IdNowError
        onSwitchToPhone={() => {
          setState('download-app');
          segment.track('onboarding_idnow_error_device_switched');
          tryAgain();
        }}
        onTryAgain={() => {
          segment.track('onboarding_idnow_error_retry_started');
          tryAgain();
        }}
        reason={reason}
      />
    );
  }
  if (status === 'waiting_results') {
    return <FraudSuspicion />;
  }
  if (status === 'rejected') {
    return <FraudConfirmed />;
  }

  if (isMobile) {
    // Mobile statuses
    return (
      <LandingMobile
        hasGermanResidencePermit={hasGermanResidencePermit}
        onContinue={link => {
          segment.track('onboarding_idnow_download_link_clicked', { link });
        }}
        token={token}
      />
    );
  }

  // Desktop statuses
  if (status === 'on_call') {
    return <StartedOnPhone />;
  }
  if (status === 'succeeded') {
    return null;
  }
  // status === 'pending'
  if (state === 'video-call' && browser?.name === 'Safari') {
    return (
      <WaitingPopup
        goBack={() => {
          setState('landing');
        }}
        src={src}
      />
    );
  }
  if (state === 'video-call') {
    return <Iframe src={src} />;
  }
  if (state === 'download-app') {
    return (
      <DownloadApp
        onBack={() => {
          setState('landing');
        }}
        onSendSms={onSendSMS}
        qrCodeUrl={qrCodeUrl}
        token={token}
      />
    );
  }
  return (
    <>
      <LandingDesktop
        hasGermanResidencePermit={hasGermanResidencePermit}
        onBack={onBack}
        onContinueComputer={() => {
          setState('consent');
        }}
        onContinuePhone={() => {
          segment.track('onboarding_idnow_device_switched');
          setState('download-app');
        }}
      />

      <ConsentPopup
        isOpen={state === 'consent'}
        onCancel={() => {
          setState('landing');
        }}
        onContinue={() => {
          segment.track('onboarding_idnow_call_started');
          setState('video-call');
        }}
      />
    </>
  );
}
